import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";

// ── Paleta Zafiro ─────────────────────────────────────────────────────────
const ACCENT  = "#35627A";
const RED     = "#B46258";
const VIOLET  = "#A6A9D0";
const GRAY    = "#8E9A98";
const BORDER  = "#e2e5e4";
const BG      = "#F5F5F5";

const PESTEL_COLORS = {
  "Político":    "#35627A",
  "Económico":   "#059669",
  "Social":      "#B46258",
  "Tecnológico": "#A6A9D0",
  "Ambiental":   "#8E9A98",
  "Legal":       "#E5AEA9",
};

const HORIZON_ORDER = ["Corto plazo", "Mediano plazo", "Largo plazo"];

// ── Timeline panel ────────────────────────────────────────────────────────
function TimelinePanel({ scenario, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!scenario || !ref.current) return;
    const el = ref.current;
    const W  = el.clientWidth || 400;
    const H  = 260;
    const PAD = { top: 30, right: 20, bottom: 40, left: 20 };

    d3.select(el).selectAll("*").remove();
    const svg = d3.select(el).append("svg").attr("width", W).attr("height", H);
    const g   = svg.append("g").attr("transform", `translate(${PAD.left},${PAD.top})`);
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;

    // X scale — 3 horizontes
    const xScale = d3.scaleBand()
      .domain(HORIZON_ORDER)
      .range([0, innerW])
      .padding(0.1);

    // Y scale — probabilidad de impacto
    const yScale = d3.scaleLinear()
      .domain([0, 1])
      .range([innerH, 0]);

    // Columnas de fondo
    HORIZON_ORDER.forEach((h, i) => {
      g.append("rect")
        .attr("x", xScale(h))
        .attr("y", 0)
        .attr("width", xScale.bandwidth())
        .attr("height", innerH)
        .attr("fill", i % 2 === 0 ? `${ACCENT}08` : "transparent");

      g.append("text")
        .attr("x", xScale(h) + xScale.bandwidth() / 2)
        .attr("y", -10)
        .attr("text-anchor", "middle")
        .attr("fill", ACCENT)
        .attr("font-size", 9)
        .attr("font-weight", "600")
        .attr("font-family", "JetBrains Mono, monospace")
        .text(h.toUpperCase());
    });

    // Línea de referencia impacto 50%
    g.append("line")
      .attr("x1", 0).attr("x2", innerW)
      .attr("y1", yScale(0.5)).attr("y2", yScale(0.5))
      .attr("stroke", BORDER).attr("stroke-dasharray", "3,2").attr("stroke-width", 1);

    g.append("text")
      .attr("x", innerW).attr("y", yScale(0.5) - 4)
      .attr("text-anchor", "end")
      .attr("fill", GRAY).attr("font-size", 8)
      .text("50% impacto");

    // Señales como puntos
    const jitter = d3.randomNormal(0, 8);
    scenario.signals.forEach(sig => {
      const h      = sig.time_horizon || "Largo plazo";
      const impact = Math.max(0.05, Math.min(0.95, +sig.impact_probability || 0.5));
      const color  = PESTEL_COLORS[sig.pestel_category] || ACCENT;
      const isOpp  = sig.classification === "Oportunidad";
      const cx     = (xScale(h) || 0) + xScale.bandwidth() / 2 + jitter();
      const cy     = yScale(impact);

      const node = g.append("g")
        .attr("transform", `translate(${cx},${cy})`)
        .style("cursor", "pointer");

      node.append("circle")
        .attr("r", 5 + impact * 4)
        .attr("fill", color)
        .attr("fill-opacity", isOpp ? 0.3 : 0.15)
        .attr("stroke", color)
        .attr("stroke-width", 1.5);

      node.append("title").text(
        `${sig.Title}\n${sig.pestel_category} · ${sig.classification}\nImpacto: ${Math.round(impact * 100)}%`
      );
    });

    // Eje Y simplificado
    [0, 0.5, 1].forEach(v => {
      g.append("text")
        .attr("x", -4).attr("y", yScale(v) + 4)
        .attr("text-anchor", "end")
        .attr("fill", GRAY).attr("font-size", 8)
        .text(`${Math.round(v * 100)}%`);
    });

  }, [scenario]);

  if (!scenario) return null;

  const isOpp = scenario.scenario_type === "Oportunidad estratégica";

  return (
    <div style={{
      position: "fixed", top: 0, right: 0, bottom: 0,
      width: 420, background: "#fff",
      borderLeft: `1px solid ${BORDER}`,
      boxShadow: "-4px 0 24px rgba(53,98,122,0.12)",
      display: "flex", flexDirection: "column",
      zIndex: 1000,
      animation: "slideIn 0.2s ease",
    }}>
      <style>{`
        @keyframes slideIn { from { transform: translateX(420px); } to { transform: translateX(0); } }
      `}</style>

      {/* Header */}
      <div style={{
        padding: "20px",
        borderBottom: `1px solid ${BORDER}`,
        background: isOpp ? `${ACCENT}08` : `${RED}08`,
        flexShrink: 0,
      }}>
        <button onClick={onClose} style={{
          position: "absolute", top: 12, right: 12,
          background: "rgba(255,255,255,0.8)", border: `1px solid ${BORDER}`,
          borderRadius: "50%", width: 28, height: 28,
          cursor: "pointer", fontSize: 14, color: "#555",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>×</button>

        <div style={{
          fontSize: 9, fontFamily: "JetBrains Mono, monospace",
          color: GRAY, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6
        }}>Escenario</div>

        <div style={{ fontSize: 15, fontWeight: 700, color: "#111", marginBottom: 8 }}>
          {scenario.cluster}
        </div>

        <span style={{
          fontSize: 10, fontWeight: 600, fontFamily: "JetBrains Mono, monospace",
          padding: "3px 10px", borderRadius: 20,
          background: isOpp ? `${ACCENT}15` : `${RED}15`,
          color: isOpp ? "#264a5e" : "#8a3f35",
          border: `1px solid ${isOpp ? `${ACCENT}30` : `${RED}30`}`,
        }}>{scenario.scenario_type}</span>
      </div>

      {/* KPIs */}
      <div style={{
        display: "flex", gap: 0,
        borderBottom: `1px solid ${BORDER}`,
        flexShrink: 0,
      }}>
        {[
          { label: "Señales",      value: scenario.signals.length,              color: "#111" },
          { label: "ROII",         value: scenario.roii,                        color: ACCENT },
          { label: "RMI",          value: scenario.rmi,                         color: scenario.rmi >= 0.7 ? ACCENT : RED },
          { label: "Impacto prom", value: `${Math.round(scenario.avg_impact * 100)}%`, color: "#111" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            flex: 1, padding: "12px 10px", textAlign: "center",
            borderRight: `1px solid ${BORDER}`,
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
            <div style={{ fontSize: 9, color: GRAY, marginTop: 4, fontFamily: "JetBrains Mono, monospace" }}>
              {label.toUpperCase()}
            </div>
          </div>
        ))}
      </div>

      {/* Timeline */}
      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
        <div style={{
          fontSize: 9, fontFamily: "JetBrains Mono, monospace",
          color: GRAY, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8
        }}>Horizonte temporal de impacto</div>
        <div ref={ref} style={{ width: "100%" }} />
      </div>

      {/* Leyenda PESTEL */}
      <div style={{ padding: "12px 20px", flexShrink: 0 }}>
        <div style={{
          fontSize: 9, fontFamily: "JetBrains Mono, monospace",
          color: GRAY, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8
        }}>Categorías PESTEL</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {Object.entries(PESTEL_COLORS).map(([cat, color]) => {
            const count = scenario.signals.filter(s => s.pestel_category === cat).length;
            if (!count) return null;
            return (
              <div key={cat} style={{
                display: "flex", alignItems: "center", gap: 4,
                padding: "2px 8px", borderRadius: 20,
                background: `${color}12`, border: `1px solid ${color}30`,
                fontSize: 10, color: "#333",
              }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
                {cat} ({count})
              </div>
            );
          })}
        </div>
      </div>

      {/* Lista de señales top */}
      <div style={{ flex: 1, overflowY: "auto", padding: "0 20px 16px" }}>
        <div style={{
          fontSize: 9, fontFamily: "JetBrains Mono, monospace",
          color: GRAY, textTransform: "uppercase", letterSpacing: 1,
          marginBottom: 8, marginTop: 4
        }}>Señales principales</div>
        {scenario.signals
          .sort((a, b) => (+b.impact_probability || 0) - (+a.impact_probability || 0))
          .slice(0, 8)
          .map((sig, i) => {
            const isOpp = sig.classification === "Oportunidad";
            const impact = Math.round((+sig.impact_probability || 0) * 100);
            return (
              <div key={i} style={{
                padding: "8px 0",
                borderBottom: `1px solid ${BORDER}`,
                fontSize: 12,
              }}>
                <div style={{
                  display: "flex", justifyContent: "space-between",
                  alignItems: "flex-start", gap: 8
                }}>
                  <div style={{ color: "#111", fontWeight: 500, lineHeight: 1.3, flex: 1 }}>
                    {sig.Title}
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 600, whiteSpace: "nowrap",
                    color: isOpp ? "#264a5e" : "#8a3f35",
                  }}>{isOpp ? "↑" : "↓"} {impact}%</span>
                </div>
                <div style={{ fontSize: 10, color: GRAY, marginTop: 3 }}>
                  {sig.pestel_category} · {sig.time_horizon}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}

// ── Bubble Chart ROII vs RMI ──────────────────────────────────────────────
function BubbleChart({ scenarios, onSelect, selected }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!scenarios.length || !ref.current) return;
    const el = ref.current;
    const W  = el.clientWidth || 680;
    const H  = 420;
    const PAD = { top: 40, right: 40, bottom: 50, left: 50 };

    d3.select(el).selectAll("*").remove();
    const svg = d3.select(el).append("svg").attr("width", W).attr("height", H);
    const g   = svg.append("g").attr("transform", `translate(${PAD.left},${PAD.top})`);
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;

    const xVals = scenarios.map(s => s.umap_cx);
    const yVals = scenarios.map(s => s.umap_cy);
    const xScale = d3.scaleLinear()
      .domain([Math.min(...xVals) - 1, Math.max(...xVals) + 1])
      .range([0, innerW]);
    const yScale = d3.scaleLinear()
      .domain([Math.min(...yVals) - 1, Math.max(...yVals) + 1])
      .range([innerH, 0]);
    const rScale = d3.scaleSqrt()
      .domain([0, Math.max(...scenarios.map(s => s.signals.length))])
      .range([14, 48]);

    // Cuadrantes fondo
    const quads = [
      { x: 0,         y: 0,          w: innerW / 2, h: innerH / 2, label: "Alto riesgo\nbaja oportunidad", color: `${RED}06` },
      { x: innerW / 2, y: 0,         w: innerW / 2, h: innerH / 2, label: "Alto riesgo\nalta oportunidad", color: `${VIOLET}08` },
      { x: 0,          y: innerH / 2, w: innerW / 2, h: innerH / 2, label: "Bajo riesgo\nbaja oportunidad", color: `${GRAY}06` },
      { x: innerW / 2, y: innerH / 2, w: innerW / 2, h: innerH / 2, label: "Bajo riesgo\nalta oportunidad", color: `${ACCENT}06` },
    ];

    quads.forEach(q => {
      g.append("rect")
        .attr("x", q.x).attr("y", q.y)
        .attr("width", q.w).attr("height", q.h)
        .attr("fill", q.color);
      q.label.split("\n").forEach((line, li) => {
        g.append("text")
          .attr("x", q.x + q.w / 2)
          .attr("y", q.y + q.h / 2 + (li - 0.5) * 13)
          .attr("text-anchor", "middle")
          .attr("fill", "#aaa").attr("font-size", 9)
          .attr("font-family", "JetBrains Mono, monospace")
          .text(line.toUpperCase());
      });
    });

    // Líneas divisoras
    g.append("line")
      .attr("x1", innerW / 2).attr("x2", innerW / 2)
      .attr("y1", 0).attr("y2", innerH)
      .attr("stroke", BORDER).attr("stroke-width", 1).attr("stroke-dasharray", "4,3");
    g.append("line")
      .attr("x1", 0).attr("x2", innerW)
      .attr("y1", innerH / 2).attr("y2", innerH / 2)
      .attr("stroke", BORDER).attr("stroke-width", 1).attr("stroke-dasharray", "4,3");

    // Ejes
    const xAxis = d3.axisBottom(xScale).ticks(5).tickSize(-innerH);
    const yAxis = d3.axisLeft(yScale).ticks(5).tickSize(-innerW)
      .tickFormat(d => `${Math.round(d * 100)}%`);

    g.append("g").attr("transform", `translate(0,${innerH})`).call(xAxis)
      .call(a => a.select(".domain").remove())
      .call(a => a.selectAll(".tick line").attr("stroke", BORDER).attr("stroke-dasharray", "2,2"))
      .call(a => a.selectAll(".tick text").attr("fill", GRAY).attr("font-size", 10));

    g.append("g").call(yAxis)
      .call(a => a.select(".domain").remove())
      .call(a => a.selectAll(".tick line").attr("stroke", BORDER).attr("stroke-dasharray", "2,2"))
      .call(a => a.selectAll(".tick text").attr("fill", GRAY).attr("font-size", 10));

    // Labels ejes
    g.append("text")
      .attr("x", innerW / 2).attr("y", innerH + 40)
      .attr("text-anchor", "middle")
      .attr("fill", ACCENT).attr("font-size", 11).attr("font-weight", "600")
      .text("← RMI (Mitigación de riesgo) →");

    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -innerH / 2).attr("y", -38)
      .attr("text-anchor", "middle")
      .attr("fill", ACCENT).attr("font-size", 11).attr("font-weight", "600")
      .text("↑ ROII (Retorno sobre inversión)");

    // Burbujas
    scenarios.forEach((sc, i) => {
      const jitter = (i * 37.3) % 30 - 15;
      const cx = xScale(sc.umap_cx);
      const cy = yScale(sc.umap_cy);
      const r       = rScale(sc.signals.length);
      const isOpp   = sc.scenario_type === "Oportunidad estratégica";
      const color   = isOpp ? ACCENT : RED;
      const isSel   = selected?.cluster === sc.cluster;

      const node = g.append("g")
        .attr("transform", `translate(${cx},${cy})`)
        .style("cursor", "pointer");

      // Halo seleccionado
      if (isSel) {
        node.append("circle")
          .attr("r", r + 6)
          .attr("fill", "none")
          .attr("stroke", color)
          .attr("stroke-width", 2)
          .attr("stroke-opacity", 0.5);
      }

      // Burbuja
      node.append("circle")
        .attr("r", r)
        .attr("fill", color)
        .attr("fill-opacity", isSel ? 0.4 : 0.2)
        .attr("stroke", color)
        .attr("stroke-width", isSel ? 2 : 1.5)
        .on("click", () => onSelect(sc))
        .on("mouseover", function() { d3.select(this).attr("fill-opacity", 0.4); })
        .on("mouseout",  function() { d3.select(this).attr("fill-opacity", isSel ? 0.4 : 0.2); });

      // Label
      const label = sc.cluster;
      node.append("text")
        .attr("text-anchor", "middle")
        .attr("dy", "0.35em")
        .attr("fill", color)
        .attr("font-size", Math.max(8, Math.min(11, r / 3)))
        .attr("font-weight", "600")
        .attr("font-family", "Inter, sans-serif")
        .attr("pointer-events", "none")
        .text(label);

      // Tooltip
      node.append("title").text(
        `${sc.cluster}\n${sc.scenario_type}\nROII: ${sc.roii} | RMI: ${sc.rmi}\nSeñales: ${sc.signals.length}\nClic para ver detalle`
      );
    });

    // Leyenda tamaño
    const legendG = svg.append("g").attr("transform", `translate(${W - PAD.right - 10}, ${PAD.top + 10})`);
    legendG.append("text")
      .attr("x", 0).attr("y", 0)
      .attr("text-anchor", "end")
      .attr("fill", GRAY).attr("font-size", 9)
      .attr("font-family", "JetBrains Mono, monospace")
      .text("TAMAÑO = N° SEÑALES");

  }, [scenarios, selected]);

  return <div ref={ref} style={{ width: "100%" }} />;
}

// ── Main Scenarios component ──────────────────────────────────────────────
export default function Scenarios({ scenarios = [] }) {
  const [selected, setSelected] = useState(null);

  // Resumen consolidado
  const avgROII = scenarios.length
    ? Math.round(scenarios.reduce((a, b) => a + b.roii, 0) / scenarios.length * 100) / 100
    : 0;
  const avgRMI = scenarios.length
    ? Math.round(scenarios.reduce((a, b) => a + b.rmi, 0) / scenarios.length * 100) / 100
    : 0;
  const bestROII  = scenarios.length ? scenarios.reduce((a, b) => a.roii > b.roii ? a : b) : null;
  const worstRMI  = scenarios.length ? scenarios.reduce((a, b) => a.rmi  < b.rmi  ? a : b) : null;

  if (!scenarios.length) {
    return (
      <div style={{
        background: "#fff", border: `1px solid ${BORDER}`,
        borderRadius: 10, padding: 32, textAlign: "center",
        color: GRAY, fontSize: 13,
      }}>
        No hay escenarios disponibles. Ejecuta el análisis primero.
      </div>
    );
  }

  const cardStyle = {
    background: "#fff", border: `1px solid ${BORDER}`,
    borderRadius: 10, padding: "18px 20px", marginBottom: 14,
    boxShadow: "0 1px 3px rgba(53,98,122,0.06)",
  };
  const titleStyle = {
    fontSize: 11, color: GRAY, textTransform: "uppercase",
    letterSpacing: ".8px", marginBottom: 14,
    display: "flex", alignItems: "center", gap: 6, fontWeight: 600,
  };

  return (
    <div onClick={() => setSelected(null)}>

      {/* Resumen consolidado */}
      <div style={cardStyle}>
        <div style={titleStyle}><span>📊</span> Resumen consolidado</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div style={{
            flex: "1 1 120px", background: `${ACCENT}07`,
            border: `1px solid ${ACCENT}25`, borderRadius: 8, padding: "12px 14px",
          }}>
            <div style={{ fontSize: 9, fontFamily: "JetBrains Mono, monospace", color: GRAY, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>ROII Promedio</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: ACCENT }}>{avgROII}</div>
            <div style={{ fontSize: 10, color: GRAY, marginTop: 2 }}>retorno / riesgo</div>
          </div>
          <div style={{
            flex: "1 1 120px",
            background: avgRMI >= 0.7 ? `${ACCENT}07` : `${RED}07`,
            border: `1px solid ${avgRMI >= 0.7 ? ACCENT : RED}25`,
            borderRadius: 8, padding: "12px 14px",
          }}>
            <div style={{ fontSize: 9, fontFamily: "JetBrains Mono, monospace", color: GRAY, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>RMI Promedio</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: avgRMI >= 0.7 ? ACCENT : RED }}>{avgRMI}</div>
            <div style={{ fontSize: 10, color: GRAY, marginTop: 2 }}>mitigación de riesgo</div>
          </div>
          {bestROII && (
            <div style={{
              flex: "1 1 160px", background: "rgba(5,150,105,0.06)",
              border: "1px solid rgba(5,150,105,0.2)", borderRadius: 8, padding: "12px 14px",
            }}>
              <div style={{ fontSize: 9, fontFamily: "JetBrains Mono, monospace", color: GRAY, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Mejor ROII</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#065f46", lineHeight: 1.3 }}>{bestROII.cluster}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#065f46", marginTop: 4 }}>{bestROII.roii}</div>
            </div>
          )}
          {worstRMI && (
            <div style={{
              flex: "1 1 160px", background: `${RED}06`,
              border: `1px solid ${RED}25`, borderRadius: 8, padding: "12px 14px",
            }}>
              <div style={{ fontSize: 9, fontFamily: "JetBrains Mono, monospace", color: GRAY, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Mayor Riesgo</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#8a3f35", lineHeight: 1.3 }}>{worstRMI.cluster}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#8a3f35", marginTop: 4 }}>RMI {worstRMI.rmi}</div>
            </div>
          )}
        </div>
      </div>

      {/* Mapa de burbujas */}
      <div style={cardStyle} onClick={e => e.stopPropagation()}>
        <div style={titleStyle}><span>🔵</span> Mapa estratégico — ROII vs RMI</div>
        <div style={{ fontSize: 11, color: GRAY, marginBottom: 12 }}>
          Clic en una burbuja para ver el detalle y horizonte temporal de impacto.
          El tamaño indica el número de señales del escenario.
        </div>
        <BubbleChart
          scenarios={scenarios}
          onSelect={sc => setSelected(sc)}
          selected={selected}
        />
        {/* Leyenda */}
        <div style={{ display: "flex", gap: 16, marginTop: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: GRAY }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: `${ACCENT}30`, border: `1.5px solid ${ACCENT}` }} />
            Oportunidad estratégica
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: GRAY }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: `${RED}30`, border: `1.5px solid ${RED}` }} />
            Crisis potencial
          </div>
        </div>
      </div>

      {/* Panel lateral */}
      {selected && (
        <>
          <div
            onClick={() => setSelected(null)}
            style={{
              position: "fixed", inset: 0,
              background: "rgba(0,0,0,0.15)",
              zIndex: 999,
            }}
          />
          <TimelinePanel
            scenario={selected}
            onClose={() => setSelected(null)}
          />
        </>
      )}
    </div>
  );
}
